import {
  Home,
  Users,
  Calendar,
  Heart,
  ClipboardCheck,
  MessageCircle,
  MessageSquare,
  Settings,
  Building2,
  BarChart3,
  CreditCard,
  Activity,
  FileText,
  User,
  UserCheck,
  Clock,
  BookOpen,
  UserCircle,
  ShieldCheck,
  Link2,
  type LucideIcon,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Single source of truth for in-app navigation.
//
// The single app shell (components/Layout.tsx) builds its menu from the
// structures here, so member, church-admin, and super-admin navigation stay in
// one place (and every href below points at a route that actually exists in
// App.tsx — no more dead `/admin/*` links).
//
// Visibility is derived from the permission source of truth (usePermissions);
// this module only describes the menu, it does not decide access. Route guards
// remain the enforcement layer.
// ---------------------------------------------------------------------------

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

// Community features available to any approved member.
export const MEMBER_NAV: NavGroup[] = [
  {
    label: 'Community',
    items: [
      { name: 'Dashboard', href: '/', icon: Home },
      { name: 'Events', href: '/calendar-view', icon: Calendar },
      { name: 'Prayer Wall', href: '/prayers', icon: Heart },
      { name: 'Directory', href: '/directory', icon: BookOpen },
      { name: 'Business Directory', href: '/public-business-directory', icon: Building2 },
      { name: 'Chat', href: '/chat', icon: MessageCircle },
    ],
  },
];

// Church-running features for church admins (and super admins acting on a church).
export const CHURCH_ADMIN_NAV: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/', icon: Home },
      { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'People',
    items: [
      { name: 'Members', href: '/members', icon: Users },
      { name: 'Directory', href: '/directory', icon: BookOpen },
      { name: 'Change Requests', href: '/directory/change-requests', icon: FileText },
      { name: 'Guests', href: '/guests', icon: UserCheck },
    ],
  },
  {
    label: 'Ministry',
    items: [
      { name: 'Services', href: '/services', icon: Clock },
      { name: 'Small Groups', href: '/small-groups', icon: Heart },
      { name: 'Volunteers', href: '/volunteers', icon: UserCheck },
      { name: 'Prayer Wall', href: '/prayers', icon: Heart },
      { name: 'Attendance', href: '/attendance', icon: ClipboardCheck },
    ],
  },
  {
    label: 'Communication',
    items: [
      { name: 'Chat', href: '/chat', icon: MessageCircle },
      { name: 'SMS Messaging', href: '/sms', icon: MessageSquare },
      { name: 'Calendar', href: '/calendar-view', icon: Calendar },
    ],
  },
  {
    label: 'Administration',
    items: [
      { name: 'Business Directory', href: '/public-business-directory', icon: Building2 },
      { name: 'Giving & Finance', href: '/giving', icon: CreditCard },
      { name: 'Directory Settings', href: '/directory/settings', icon: Settings },
      { name: 'Church Settings', href: '/settings', icon: Settings },
    ],
  },
];

// Platform-level items, super-admin only. Visually separated from church-running.
// Every href maps to a genuine `/superadmin/*` (or `/churches`) route.
export const PLATFORM_NAV: NavGroup = {
  label: 'Platform',
  items: [
    { name: 'All Churches', href: '/churches', icon: Building2 },
    { name: 'Global Analytics', href: '/superadmin/analytics', icon: BarChart3 },
    { name: 'Reports', href: '/superadmin/reports', icon: FileText },
    { name: 'Audit Trail', href: '/superadmin/audit', icon: Activity },
    { name: 'Integrations', href: '/superadmin/integrations', icon: Link2 },
    { name: 'Platform Users', href: '/superadmin/users', icon: ShieldCheck },
    { name: 'Platform Settings', href: '/superadmin/settings', icon: Settings },
  ],
};

// Personal section shown in the member/church-admin shell (not super admins).
export const PERSONAL_NAV: NavItem[] = [
  { name: 'My Profile', href: '/profile', icon: UserCircle },
  { name: 'Directory Profile', href: '/directory/my-profile', icon: User },
];

// Per-church drill-down used by the super-admin church tree.
export function getChurchNavItems(churchId: string): NavItem[] {
  return [
    { name: 'Overview', href: `/church/${churchId}`, icon: Home },
    { name: 'Members', href: `/church/${churchId}/members`, icon: User },
    { name: 'Groups', href: `/church/${churchId}/groups`, icon: Users },
    { name: 'Prayer Wall', href: `/church/${churchId}/prayer-wall`, icon: Heart },
    { name: 'Volunteers', href: `/church/${churchId}/volunteers`, icon: UserCheck },
    { name: 'Events', href: `/church/${churchId}/events`, icon: Calendar },
    { name: 'Attendance', href: `/church/${churchId}/attendance`, icon: ClipboardCheck },
    { name: 'Giving / Donations', href: `/church/${churchId}/giving`, icon: CreditCard },
    { name: 'Finances / Budgets', href: `/church/${churchId}/budgets`, icon: BarChart3 },
    { name: 'Messages', href: `/church/${churchId}/messages`, icon: MessageSquare },
    { name: 'Media / LiveStream', href: `/church/${churchId}/media`, icon: Activity },
    { name: 'Settings', href: `/church/${churchId}/settings`, icon: Settings },
  ];
}

export interface PrimaryNavInput {
  isSuperAdmin: boolean;
  isChurchAdmin: boolean;
}

// The grouped menu for the member/church-admin shell. Super admins get the full
// church-running menu plus a separated Platform group.
export function buildPrimaryNav({ isSuperAdmin, isChurchAdmin }: PrimaryNavInput): NavGroup[] {
  if (isSuperAdmin) return [...CHURCH_ADMIN_NAV, PLATFORM_NAV];
  if (isChurchAdmin) return CHURCH_ADMIN_NAV;
  return MEMBER_NAV;
}
